import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';

import type { DelayedQuote, TickerDetail } from '@benzinga/quotes-manager';
import type { CompanyProfile, Company, Financials } from '@benzinga/securities-manager';

countries.registerLocale(enLocale);

const clean = (value?: string | null): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

const toAlpha2Country = (value?: string | null): string | undefined => {
  const trimmed = clean(value);
  if (!trimmed) return undefined;
  const upper = trimmed.toUpperCase();
  if (upper.length === 2) return countries.isValid(upper) ? upper : undefined;
  if (upper.length === 3) return countries.alpha3ToAlpha2(upper) || undefined;
  return countries.getAlpha2Code(trimmed, 'en') || undefined;
};

const buildPostalAddress = (profile?: CompanyProfile | null) => {
  if (!profile) return undefined;
  const streetAddress = clean(profile.address1);
  const addressLocality = clean(profile.city);
  const addressRegion = clean(profile.province);
  const postalCode = clean(profile.postalCode);
  const addressCountry = toAlpha2Country(profile.country);
  if (!streetAddress && !addressLocality && !addressRegion && !postalCode && !addressCountry) {
    return undefined;
  }
  return {
    '@type': 'PostalAddress',
    ...(streetAddress && { streetAddress }),
    ...(addressLocality && { addressLocality }),
    ...(addressRegion && { addressRegion }),
    ...(postalCode && { postalCode }),
    ...(addressCountry && { addressCountry }),
  };
};

const buildIdentifiers = (company?: Company | null) => {
  if (!company) return undefined;
  const identifiers = (
    [
      ['CIK', clean(company.cik)],
      ['ISIN', clean(company.isin)],
      ['CUSIP', clean(company.cusip)],
    ] as const
  )
    .filter(([, value]) => value)
    .map(([propertyID, value]) => ({ '@type': 'PropertyValue', propertyID, value: value as string }));
  return identifiers.length ? identifiers : undefined;
};

export const buildQuoteCorporationSchema = (
  symbol: string,
  richQuoteData: DelayedQuote | null,
  tickerDetails: TickerDetail | null,
  canonical: string,
  fundamentals?: Financials | null,
  logoUrl?: string | null,
) => {
  const profile = fundamentals?.companyProfile;
  const company = fundamentals?.company;

  const legalName = clean(profile?.legalName);
  const logo = clean(logoUrl);
  const sameAs = clean(profile?.homepage);
  const telephone = clean(profile?.phone);
  const address = buildPostalAddress(profile);
  const identifier = buildIdentifiers(company);
  const employees = profile?.totalEmployees;
  const numberOfEmployees = typeof employees === 'number' && employees > 0 ? employees : undefined;

  return {
    '@context': 'https://schema.org',
    '@type': 'Corporation',
    description: tickerDetails?.company?.longDescription ?? '',
    name: richQuoteData?.companyStandardName ?? '',
    tickerSymbol: symbol,
    url: canonical ?? '',
    ...(legalName && { legalName }),
    ...(logo && { logo, image: logo }),
    ...(sameAs && { sameAs }),
    ...(address && { address }),
    ...(telephone && { telephone }),
    ...(numberOfEmployees && {
      numberOfEmployees: { '@type': 'QuantitativeValue', value: numberOfEmployees },
    }),
    ...(identifier && { identifier }),
  };
};
